import React, { useEffect, useRef, useState } from "react";
import "./PitchDeck.css";

const PitchDeck: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (isScrolling) return;

      e.preventDefault();
      setIsScrolling(true);

      const delta = e.deltaY > 0 ? 1 : -1;
      const newSlide = Math.max(0, Math.min(11, currentSlide + delta));

      setCurrentSlide(newSlide);
      container.scrollTo({
        left: newSlide * window.innerWidth,
        behavior: "smooth",
      });

      setTimeout(() => setIsScrolling(false), 800);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isScrolling) return;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setIsScrolling(true);
        const newSlide = Math.min(11, currentSlide + 1);
        setCurrentSlide(newSlide);
        container.scrollTo({
          left: newSlide * window.innerWidth,
          behavior: "smooth",
        });
        setTimeout(() => setIsScrolling(false), 800);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setIsScrolling(true);
        const newSlide = Math.max(0, currentSlide - 1);
        setCurrentSlide(newSlide);
        container.scrollTo({
          left: newSlide * window.innerWidth,
          behavior: "smooth",
        });
        setTimeout(() => setIsScrolling(false), 800);
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentSlide, isScrolling]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const slide = Math.round(container.scrollLeft / window.innerWidth);
      setCurrentSlide(slide);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Create particles
    const particlesContainer = document.getElementById("particles");
    if (!particlesContainer) return;

    for (let i = 0; i < 30; i++) {
      const particle = document.createElement("div");
      particle.className = "particle";
      particle.style.left = Math.random() * 100 + "%";
      particle.style.top = Math.random() * 100 + "%";
      particle.style.animationDelay = Math.random() * 15 + "s";
      particle.style.animationDuration = Math.random() * 10 + 10 + "s";
      particlesContainer.appendChild(particle);
    }

    return () => {
      if (particlesContainer) {
        particlesContainer.innerHTML = "";
      }
    };
  }, []);

  return (
    <div className="pitch-deck-container" ref={containerRef}>
      {/* Navigation Dots */}
      <div className="slide-indicators">
        {Array.from({ length: 12 }).map((_, index) => (
          <button
            key={index}
            className={`indicator ${currentSlide === index ? "active" : ""}`}
            onClick={() => {
              setCurrentSlide(index);
              containerRef.current?.scrollTo({
                left: index * window.innerWidth,
                behavior: "smooth",
              });
            }}
          />
        ))}
      </div>

      {/* Slide 1: Hero */}
      <section className="slide hero-slide">
        <div className="background-grid"></div>
        <div className="particles" id="particles"></div>
        <nav className="pitch-nav">
          <div className="logo">Crypsey</div>
          <a href="#ask" className="cta-button">
            View Investment Terms
          </a>
        </nav>
        <div className="hero-content">
          <h1 className="fade-in-up">
            Building Canada's
            <br />
            <span className="gradient-text">Bloomberg Terminal</span>
            <br />
            for Mortgage Data
          </h1>
          <p className="hero-subtitle fade-in-up delay-1">
            Blockchain-Verified | Real-Time Analytics | API-First Platform
          </p>
          <div className="cta-buttons fade-in-up delay-2">
            <a href="#ask" className="cta-button">
              Schedule Investor Call
            </a>
            <a href="#" className="secondary-button">
              Download Full Deck
            </a>
          </div>
          <div className="hero-metrics fade-in-up delay-3">
            <div className="metric">
              <div className="metric-value">$2.1T</div>
              <div className="metric-label">Market Size</div>
            </div>
            <div className="metric">
              <div className="metric-value">300+</div>
              <div className="metric-label">Target Lenders</div>
            </div>
            <div className="metric">
              <div className="metric-value">$250K</div>
              <div className="metric-label">Raising</div>
            </div>
            <div className="metric">
              <div className="metric-value">32x</div>
              <div className="metric-label">ROI Projection</div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 2: The Problem */}
      <section className="slide problem-slide">
        <h2 className="slide-title">THE PROBLEM</h2>
        <div className="problem-header">
          <h3>Institutional Investors Face Critical Information Gap</h3>
          <p>
            Today, mortgage investors are making{" "}
            <strong>multi-million dollar decisions</strong> with:
          </p>
          <ul className="problem-bullets">
            <li>
              <strong>Zero visibility</strong> into real-time default rates
            </li>
            <li>
              <strong>No access</strong> to prepayment behavior data
            </li>
            <li>
              <strong>Limited understanding</strong> of alternative lending
              performance
            </li>
            <li>
              <strong>Outdated quarterly reports</strong> vs. real-time market
              conditions
            </li>
            <li>
              <strong>No tools</strong> to benchmark loan performance
            </li>
          </ul>
        </div>
        <div className="cost-impact">
          <h3>Real Cost of Information Asymmetry</h3>
          <ul>
            <li>
              <strong>Alternative lenders</strong> charging 8-12% rates with
              insufficient default data
            </li>
            <li>
              <strong>Investment funds</strong> allocating $100M+ based on gut
              feel, not analytics
            </li>
            <li>
              <strong>Underwriting decisions</strong> made without market
              benchmarks
            </li>
            <li>
              <strong>Mispriced risk</strong> across the entire $50-75B
              alternative lending segment
            </li>
          </ul>
        </div>
      </section>

      {/* Slide 3: Canadian Data Desert */}
      <section className="slide desert-slide">
        <h2 className="slide-title">
          The Canadian Data <span className="gradient-text">Desert</span>
        </h2>
        <div className="problem-grid">
          <div className="problem-card slide-in-left">
            <span className="icon">🇨🇦</span>
            <h3>Canada</h3>
            <p>
              <strong className="error-text">ZERO</strong> equivalent platforms
              <br />
              <br />
              Fragmented, siloed data across <strong>5,000+ originators</strong>
              <br />
              <br />
              Alternative lending segment <strong>completely invisible</strong>
              <br />
              <br />
              Mid-market <strong>priced out</strong> of expensive solutions
            </p>
          </div>
          <div className="problem-card slide-in-up">
            <span className="icon">🇺🇸</span>
            <h3>United States</h3>
            <p>
              <strong className="success-text">Black Knight:</strong> $1.4B
              revenue, 70%+ margins
              <br />
              <br />
              <strong className="success-text">CoreLogic:</strong> $2.1B revenue
              <br />
              <br />
              <strong className="success-text">30+</strong> established mortgage
              data providers
              <br />
              <br />
              <strong>Every major institution</strong> subscribed
            </p>
          </div>
          <div className="problem-card slide-in-right">
            <span className="icon">🎯</span>
            <h3>The Opportunity</h3>
            <p>
              <strong className="cyber-blue-text">$100M+ White Space</strong>
              <br />
              <br />
              <strong>5,000+</strong> mortgage brokerages
              <br />
              <strong>300+</strong> private lenders
              <br />
              <strong>50+</strong> PropTech companies
              <br />
              <br />
              <strong className="success-text">
                ALL operating blind. We're first to market.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* Slide 4: Customer Segments */}
      <section className="slide segments-slide">
        <h2 className="slide-title">
          Three Underserved{" "}
          <span className="gradient-text">Customer Segments</span>
        </h2>
        <div className="problem-grid">
          <div className="problem-card highlight-card slide-in-left">
            <span className="icon">🏦</span>
            <h3>1. Alternative Lenders</h3>
            <p className="segment-market">
              <strong>$50-75B Market</strong>
            </p>
            <p>
              300+ private mortgage companies operating without benchmarking
              data. Can't assess if their 2% default rate is good or terrible.
            </p>
            <div className="pricing-badge">
              <p>Will pay $2K-5K/month</p>
            </div>
          </div>
          <div className="problem-card highlight-card slide-in-up">
            <span className="icon">🚀</span>
            <h3>2. PropTech & Fintech Startups</h3>
            <p className="segment-market">
              <strong>50+ Companies</strong>
            </p>
            <p>
              Digital mortgage platforms and real estate investing apps building
              products blind to market realities. Need APIs, not PDF reports.
            </p>
            <div className="pricing-badge">
              <p>Will pay $500-2K/month</p>
            </div>
          </div>
          <div className="problem-card highlight-card slide-in-right">
            <span className="icon">💼</span>
            <h3>3. Investment Funds</h3>
            <p className="segment-market">
              <strong>Institutional Capital</strong>
            </p>
            <p>
              REITs, hedge funds, family offices allocating $10M-500M to
              mortgage investments. Desperate for due diligence data.
            </p>
            <div className="pricing-badge">
              <p>Will pay $10K-50K/month</p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 5: The Solution */}
      <section className="slide solution-slide">
        <h2 className="slide-title">THE SOLUTION</h2>
        <div className="solution-header">
          <h3>Crypsey Platform</h3>
          <p>
            A SaaS platform that{" "}
            <strong>aggregates anonymized mortgage performance data</strong>{" "}
            from brokers and lenders,
            <strong>verifies it on blockchain</strong>, and delivers{" "}
            <strong>actionable insights through modern APIs</strong>.
          </p>
        </div>
        <div className="features-grid">
          <div className="feature-card fade-in-up">
            <span className="icon">🔗</span>
            <h3>1. Data Aggregation Network</h3>
            <p>
              Partner with{" "}
              <strong>
                mortgage brokers, private lenders, and credit unions
              </strong>{" "}
              to collect anonymized loan-level performance data.
            </p>
            <ul>
              <li>
                ✓ Partner with mortgage brokers, private lenders, credit unions
              </li>
              <li>✓ Collect anonymized loan-level performance data</li>
              <li>✓ Standardize across heterogeneous sources</li>
              <li>✓ Real-time ingestion pipeline</li>
            </ul>
            <div className="feature-badge">
              <p>
                ✓ PIPEDA compliant from day 1 | ✓ All PII stripped automatically
              </p>
            </div>
            <span className="feature-metric">
              Target: 10,000+ Loans by Month 6
            </span>
          </div>
        </div>
      </section>

      {/* Slide 6: Blockchain Verification */}
      <section className="slide blockchain-slide">
        <div className="features-grid single-feature">
          <div className="feature-card fade-in-up">
            <span className="icon">⛓️</span>
            <h3>2. Blockchain Verification Layer</h3>
            <p>
              Monthly{" "}
              <strong>Merkle root published to Solana blockchain</strong>.
              Cryptographic proof of data integrity. Immutable audit trail.{" "}
              <strong>Industry-first transparency standard</strong>.
            </p>
            <ul>
              <li>✓ Monthly Merkle root published to Solana blockchain</li>
              <li>✓ Cryptographic proof of data integrity</li>
              <li>✓ Immutable audit trail</li>
              <li>✓ Industry-first transparency standard</li>
            </ul>
            <div className="feature-badge purple">
              <p>
                <strong>Why This Matters:</strong>
              </p>
              <p>
                Traditional providers say "trust us" with no verification. We
                provide cryptographic proof. Historical records can't be changed
                retroactively.
              </p>
            </div>
            <span className="feature-metric">
              Marketing Gold: "Only Blockchain-Verified Mortgage Data in Canada"
            </span>
          </div>
        </div>
      </section>

      {/* Slide 7: Analytics */}
      <section className="slide analytics-slide">
        <div className="features-grid single-feature">
          <div className="feature-card fade-in-up">
            <span className="icon">📊</span>
            <h3>3. Analytics & Distribution</h3>
            <p>
              <strong>REST API</strong> for programmatic access. Interactive
              dashboards for visual analysis. Custom report generation.
              Real-time alerts and monitoring.
            </p>
            <ul>
              <li>✓ REST API for programmatic access</li>
              <li>✓ Interactive dashboards for visual analysis</li>
              <li>✓ Custom report generation</li>
              <li>✓ Real-time alerts and monitoring</li>
            </ul>
            <span className="feature-metric">Response Time: &lt; 200ms</span>
          </div>
        </div>
        <div className="data-structure">
          <h3>
            Sample Data Structure{" "}
            <span className="success-text">(Fully Anonymized)</span>
          </h3>
          <div className="data-grid">
            <div>
              <h4>Loan Characteristics</h4>
              <ul>
                <li>Loan Amount: $450,000</li>
                <li>Interest Rate: 7.85%</li>
                <li>Loan-to-Value: 75%</li>
                <li>Property Type: Detached Home</li>
                <li>Location: Toronto (M5V prefix)</li>
                <li>Origination: 2024-09-15</li>
              </ul>
            </div>
            <div>
              <h4>Borrower Profile</h4>
              <ul>
                <li>Credit Score: 680-720</li>
                <li>Employment: Self-Employed</li>
                <li>Debt-to-Income: 38%</li>
                <li>Industry: Technology</li>
                <li className="success-text">✓ No personal identifiers</li>
              </ul>
            </div>
            <div>
              <h4>Performance Metrics</h4>
              <ul>
                <li>Payment Status: Current</li>
                <li>Days Past Due: 0</li>
                <li>Prepayment Activity: None</li>
                <li>Payment History: 14 months</li>
                <li className="success-text">✓ PIPEDA compliant</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 8: Market Opportunity */}
      <section className="slide market-slide">
        <h2 className="slide-title">
          <span className="gradient-text">Massive Market</span>, Proven Model
        </h2>
        <div className="market-stats">
          <div className="stat-card pulse-animation">
            <h3>$2.1T</h3>
            <p>Canadian Mortgage Market</p>
          </div>
          <div className="stat-card pulse-animation delay-1">
            <h3>ZERO</h3>
            <p>Competitors in Canada</p>
          </div>
          <div className="stat-card pulse-animation delay-2">
            <h3>$100M+</h3>
            <p>Total Addressable Market</p>
          </div>
        </div>
        <div className="us-validation">
          <h3>US Market Validation</h3>
          <div className="problem-grid">
            <div className="problem-card">
              <h3>Black Knight</h3>
              <p>
                <strong className="cyber-blue-text">$1.4B</strong> revenue
                annually
                <br />
                <strong className="cyber-blue-text">72%</strong> gross margin
                <br />
                Serves 90% of top US lenders
              </p>
            </div>
            <div className="problem-card">
              <h3>CoreLogic</h3>
              <p>
                <strong className="cyber-blue-text">$2.1B</strong> revenue
                annually
                <br />
                Acquired for <strong className="cyber-blue-text">$6B</strong>
                <br />
                80% recurring revenue
              </p>
            </div>
            <div className="problem-card">
              <h3>Canadian Opportunity</h3>
              <p>
                Canada = 25% of US market size
                <br />
                Suggests <strong className="success-text">
                  $300M-500M
                </strong>{" "}
                potential
                <br />
                Currently ZERO players
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 9: Business Model */}
      <section className="slide business-slide">
        <h2 className="slide-title">
          High-Margin <span className="gradient-text">SaaS Economics</span>
        </h2>
        <div className="projection-table">
          <table>
            <thead>
              <tr>
                <th>Tier</th>
                <th>Price/Month</th>
                <th>Target Customer</th>
                <th>Key Features</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Starter</td>
                <td>$499</td>
                <td>Small brokerages</td>
                <td>100 queries/mo, basic reports</td>
              </tr>
              <tr>
                <td>Professional</td>
                <td>$1,999</td>
                <td>Fintechs, mid-size lenders</td>
                <td>1,000 queries/mo, API access</td>
              </tr>
              <tr>
                <td>Business</td>
                <td>$4,999</td>
                <td>Investment funds</td>
                <td>Unlimited queries, full API</td>
              </tr>
              <tr>
                <td>Enterprise</td>
                <td>$10K-$50K</td>
                <td>Banks, institutions</td>
                <td>White-label, dedicated support</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="unit-economics">
          <h3>Unit Economics (Year 2)</h3>
          <div className="hero-metrics">
            <div className="metric">
              <div className="metric-value">$30K</div>
              <div className="metric-label">Annual Contract Value</div>
            </div>
            <div className="metric">
              <div className="metric-value">85%</div>
              <div className="metric-label">Gross Margin</div>
            </div>
            <div className="metric">
              <div className="metric-value">60:1</div>
              <div className="metric-label">LTV/CAC Ratio</div>
            </div>
            <div className="metric">
              <div className="metric-value">1.2mo</div>
              <div className="metric-label">CAC Payback</div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 10: Financial Projections */}
      <section className="slide financials-slide">
        <h2 className="slide-title">
          Path to <span className="gradient-text">Profitability</span>
        </h2>
        <div className="projection-table">
          <table>
            <thead>
              <tr>
                <th>Year</th>
                <th>Customers</th>
                <th>ARR</th>
                <th>Revenue</th>
                <th>Costs</th>
                <th>EBITDA</th>
                <th>Margin</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>40</td>
                <td>$1.2M</td>
                <td>$600K</td>
                <td>$200K</td>
                <td className="positive">$400K</td>
                <td className="positive">67%</td>
              </tr>
              <tr>
                <td>2</td>
                <td>100</td>
                <td>$2.4M</td>
                <td>$2.4M</td>
                <td>$900K</td>
                <td className="positive">$1.5M</td>
                <td className="positive">63%</td>
              </tr>
              <tr>
                <td>3</td>
                <td>250</td>
                <td>$7.5M</td>
                <td>$7.5M</td>
                <td>$2.5M</td>
                <td className="positive">$5.0M</td>
                <td className="positive">67%</td>
              </tr>
              <tr>
                <td>4</td>
                <td>500</td>
                <td>$20M</td>
                <td>$20M</td>
                <td>$7M</td>
                <td className="positive">$13M</td>
                <td className="positive">65%</td>
              </tr>
              <tr>
                <td>5</td>
                <td>1,000</td>
                <td>$50M</td>
                <td>$50M</td>
                <td>$17M</td>
                <td className="positive">$33M</td>
                <td className="positive">66%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Slide 11: Roadmap */}
      <section className="slide roadmap-slide">
        <h2 className="slide-title">
          From MVP to <span className="gradient-text">Market Leader</span>
        </h2>
        <div className="roadmap">
          <div className="roadmap-item fade-in-up">
            <h3>Month 3: MVP Launch</h3>
            <p>
              10 data partners signed, 10,000 loans in database, 5 beta
              customers using platform successfully. Core features: data
              ingestion API, PostgreSQL database, basic analytics dashboard,
              blockchain verification.
            </p>
          </div>
          <div className="roadmap-item fade-in-up delay-1">
            <h3>Month 6: Product-Market Fit</h3>
            <p>
              20 paying customers, $35K MRR, $420K ARR run rate. Added REST API
              with authentication, advanced filtering, export capabilities,
              mobile-responsive dashboards, 10+ pre-built report templates.
            </p>
          </div>
          <div className="roadmap-item fade-in-up delay-2">
            <h3>Month 12: Initial Scale</h3>
            <p>
              50+ customers, $100K MRR, $1.2M ARR, ready for Series A ($2-5M
              raise). Enhanced analytics: default rate calculations, prepayment
              speed analysis, geographic heat maps, peer group benchmarking.
            </p>
          </div>
          <div className="roadmap-item fade-in-up delay-3">
            <h3>Month 24: Market Leadership</h3>
            <p>
              100+ customers, $200K MRR, $2.4M ARR, company valued at $24M (10x
              ARR). Enterprise features: white-label dashboards, SSO
              integration, ML default prediction models, portfolio stress
              testing.
            </p>
          </div>
          <div className="roadmap-item fade-in-up delay-4">
            <h3>Year 3-4: Platform Play → Crypsey</h3>
            <p>
              Launch full investment marketplace: two-sided platform connecting
              lenders and investors. Investment matching engine, automated due
              diligence, digital deal rooms. Tokenized mortgage pools, secondary
              market trading.
            </p>
          </div>
        </div>
      </section>

      {/* Slide 12: Investment Highlights & The Ask */}
      <section className="slide ask-slide" id="ask">
        <h2 className="slide-title">
          Why This <span className="gradient-text">Will Work</span>
        </h2>
        <div className="highlights-grid">
          <div className="highlight-card fade-in-up">
            <div className="checkmark">✓</div>
            <h3>Massive Proven Market</h3>
            <p>
              $2 trillion Canadian mortgages. US comparables: Black Knight
              ($1.4B revenue), CoreLogic ($2.1B). Zero Canadian equivalent =
              white space.
            </p>
          </div>
          <div className="highlight-card fade-in-up delay-1">
            <div className="checkmark">✓</div>
            <h3>Founder-Market Fit</h3>
            <p>
              Licensed mortgage agent (industry insider). Blockchain developer
              (can build product). Rare combination of domain + technical
              expertise.
            </p>
          </div>
          <div className="highlight-card fade-in-up delay-2">
            <div className="checkmark">✓</div>
            <h3>Capital Efficient</h3>
            <p>
              $250K seed gets to $100K MRR. Can bootstrap to profitability if
              needed. SaaS economics = 85%+ gross margins. Every dollar invested
              returns 10x+.
            </p>
          </div>
          <div className="highlight-card fade-in-up delay-3">
            <div className="checkmark">✓</div>
            <h3>Network Effects Moat</h3>
            <p>
              More data → better product → more customers → more data.
              Winner-take-most market dynamics. First to 50 partners wins the
              game.
            </p>
          </div>
          <div className="highlight-card fade-in-up delay-4">
            <div className="checkmark">✓</div>
            <h3>Multiple Exit Paths</h3>
            <p>
              Strategic acquisition (Teranet, Equifax). US market entry (Black
              Knight, CoreLogic). PropTech consolidation. IPO at scale. 4+
              viable paths.
            </p>
          </div>
          <div className="highlight-card fade-in-up delay-5">
            <div className="checkmark">✓</div>
            <h3>Perfect Timing</h3>
            <p>
              Alternative lending growth accelerating. PropTech investment boom.
              Crypto capital seeking real assets. Regulatory push for
              transparency.
            </p>
          </div>
        </div>
        <div className="ask-section">
          <h2>The Investment Opportunity</h2>
          <div className="ask-amount">$250,000</div>
          <p>Seed Round Investment</p>
          <div className="terms-grid">
            <div className="term-item">
              <h4>Structure</h4>
              <p>Convertible Note</p>
            </div>
            <div className="term-item">
              <h4>Valuation Cap</h4>
              <p>$3M</p>
            </div>
            <div className="term-item">
              <h4>Discount</h4>
              <p>20%</p>
            </div>
            <div className="term-item">
              <h4>Maturity</h4>
              <p>24 months</p>
            </div>
          </div>
          <div className="returns-projection">
            <h3>Projected Returns</h3>
            <div className="return-timeline">
              <div className="return-item">
                <h4>12 Months</h4>
                <p>$1.2M</p>
                <span>ARR Run Rate</span>
              </div>
              <div className="return-item">
                <h4>24 Months</h4>
                <p>32x</p>
                <span>Return Multiple</span>
              </div>
              <div className="return-item">
                <h4>3-5 Years</h4>
                <p>133x</p>
                <span>Conservative Exit</span>
              </div>
            </div>
          </div>
          <div className="use-of-funds">
            <h3>Use of Funds</h3>
            <div className="hero-metrics">
              <div className="metric">
                <div className="metric-value">40%</div>
                <div className="metric-label">Product Development</div>
              </div>
              <div className="metric">
                <div className="metric-value">25%</div>
                <div className="metric-label">Sales & Marketing</div>
              </div>
              <div className="metric">
                <div className="metric-value">20%</div>
                <div className="metric-label">Data Partnerships</div>
              </div>
              <div className="metric">
                <div className="metric-value">15%</div>
                <div className="metric-label">Operations</div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-cta">
          <h2>
            Let's Build the Bloomberg Terminal
            <br />
            for <span className="gradient-text">Canadian Mortgages</span>
          </h2>
          <p>
            This is a category-defining opportunity in a massive, proven market
            with zero competition.
          </p>
          <div className="cta-buttons">
            <a href="mailto:patrick@crypsey.com" className="cta-button">
              Schedule Investor Call
            </a>
            <a href="#" className="secondary-button">
              Download Full Deck (PDF)
            </a>
          </div>
          <div className="footer-info">
            <p>
              Patrick Muhire · Founder & CEO, Crypsey
              <br />
              <a href="mailto:patrick@crypsey.com">patrick@crypsey.com</a> ·
              Toronto, ON
            </p>
            <p>Confidential & Proprietary © 2025 Crypsey Corp</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PitchDeck;
